import { useTranslation } from 'react-i18next';
import { Flex, Image, Table, Text } from '@chakra-ui/react';
import { FaPaperclip } from 'react-icons/fa';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { IncidentDetailItem } from '../../../services/alerts.service';

dayjs.extend(utc);

/**
 * Format an event timestamp coming from the backend.
 * Backend produces either a full ISO timestamp ("2024-01-15T14:30:00") or a
 * UTC time-only string ("14:30:00"). Convert both to local time for display.
 */
function formatEventTimestamp(timestamp: string): string {
  if (!timestamp) return '';
  const trimmed = timestamp.trim();

  if (trimmed.match(/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/)) {
    return dayjs.utc(trimmed).local().format('M/D/YYYY, h:mm A');
  }

  const timeOnlyMatch = trimmed.match(/^(\d{1,2}:\d{2}(:\d{2})?)/);
  if (timeOnlyMatch) {
    const timePart = timeOnlyMatch[1];
    const today = dayjs.utc().format('YYYY-MM-DD');
    return dayjs(`${today}T${timePart}Z`).local().format('h:mm A');
  }

  return timestamp;
}

interface AlertHistoryEventsTableProps {
  rows: IncidentDetailItem[];
}

export function AlertHistoryEventsTable({
  rows,
}: AlertHistoryEventsTableProps) {
  const { t } = useTranslation();
  return (
    <Table.Root>
      <Table.Header bg="surface.input">
        <Table.Row bg="surface.searchInput">
          {[
            { title: t('events.event', 'Event') },
            { title: t('common.type', 'Type') },
            { title: t('events.author', 'Author') },
            { title: t('events.content', 'Content') },
            { title: <FaPaperclip />, isIcon: true },
          ].map((column, idx) => (
            <Table.ColumnHeader
              key={idx}
              color="text.secondary"
              fontWeight={600}
              fontSize="md"
              borderBottom="1px solid"
              borderRight="1px solid"
              _last={{ borderRight: 'none' }}
              borderColor="border.line"
              width={column.isIcon ? '90px' : 'auto'}
              textTransform="capitalize"
            >
              <Flex alignItems="center">
                <Text mr={1}>{column.title}</Text>
              </Flex>
            </Table.ColumnHeader>
          ))}
        </Table.Row>
      </Table.Header>
      <Table.Body color="surface.primary">
        {rows.map((row, idx) => (
          <Table.Row
            key={row.id ?? idx}
            background="surface.primary"
            borderColor="border.line"
            borderBottom="1px solid"
            borderBottomColor="border.line"
          >
            <Table.Cell color="text.primary">
              {formatEventTimestamp(row.created_at)}
            </Table.Cell>
            <Table.Cell color="text.primary">{row.incident_type}</Table.Cell>
            <Table.Cell color="text.primary">{row.author_name}</Table.Cell>
            <Table.Cell color="text.primary">{row.content}</Table.Cell>
            <Table.Cell color="text.primary">
              {row.has_attachments && (
                <Image
                  src={row.image_thumbnail || row.video_thumbnail}
                  height={24}
                  width={24}
                />
              )}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
}
